"use client";

import Link from "@/infrastructure/router/Link";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@/i18n/ArtistI18n";

export function ProjectInviteLanding({
	inviteId,
}: Readonly<{ inviteId: string }>) {
	const { t } = useTranslation();
	const [secret, setSecret] = useState<string | null>(null);
	const [attempted, setAttempted] = useState(false);

	useEffect(() => {
		const fragment = window.location.hash.replace(/^#/u, "").trim();
		setSecret(fragment === "" ? null : fragment);
	}, []);

	const appLink = useMemo(() => {
		if (secret === null) return null;
		return `artist://project-invite/${encodeURIComponent(inviteId)}#${encodeURIComponent(secret)}`;
	}, [inviteId, secret]);

	useEffect(() => {
		if (appLink === null || attempted) return;
		setAttempted(true);
		const timer = window.setTimeout(() => {
			window.location.href = appLink;
		}, 180);
		return () => window.clearTimeout(timer);
	}, [appLink, attempted]);

	return (
		<main
			style={{
				minHeight: "100dvh",
				display: "grid",
				placeItems: "center",
				padding: 24,
				background:
					"radial-gradient(circle at 20% 0%, color-mix(in srgb, var(--ds-accent) 36%, transparent), transparent 42%), var(--ds-background)",
			}}
		>
			<section
				style={{
					width: "min(100%, 470px)",
					padding: 28,
					borderRadius: 30,
					background: "var(--ds-material-regular)",
					border: "1px solid var(--ds-separator)",
					boxShadow: "0 24px 80px rgba(0,0,0,.24)",
					textAlign: "center",
				}}
			>
				<div style={{ fontSize: 52, marginBottom: 14 }} aria-hidden="true">
					🤝
				</div>
				<h1 style={{ margin: 0, fontSize: 28 }}>{t("invite.title")}</h1>
				<p
					style={{
						color: "var(--ds-secondary-label)",
						lineHeight: 1.55,
					}}
				>
					{t("invite.message")}
				</p>

				{appLink === null ? (
					<p role="alert" style={{ color: "var(--ds-red)" }}>
						{t("invite.missing")}
					</p>
				) : (
					<a
						href={appLink}
						style={{
							display: "block",
							marginTop: 22,
							padding: "14px 18px",
							borderRadius: 999,
							background: "var(--ds-accent)",
							color: "white",
							fontWeight: 760,
							textDecoration: "none",
						}}
					>
						{t("invite.open")}
					</a>
				)}

				<Link
					href="/discover"
					style={{
						display: "inline-block",
						marginTop: 16,
						padding: "10px 14px",
						borderRadius: 999,
						border: "1px solid var(--ds-separator)",
						color: "var(--ds-secondary-label)",
						fontWeight: 650,
						textDecoration: "none",
					}}
				>
					{t("invite.back")}
				</Link>

				<p
					style={{
						fontSize: 12,
						color: "var(--ds-tertiary-label)",
						marginTop: 18,
					}}
				>
					{t("invite.expiry")}
				</p>
			</section>
		</main>
	);
}
